import { useMemo, useState } from "react";
import { FleetRow } from "../types";

export const useFleetSelection = (
  tableData: FleetRow[],
  _resetDeps: unknown[] = [],
  draftMode = false,
) => {
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);

  const selectedRows = useMemo(() => {
    return tableData.filter((row) => selectedKeys.includes(row.id));
  }, [selectedKeys, tableData]);

  const selectedPlateNumbers = useMemo(() => {
    return selectedRows.map((row) => row.plateNumber);
  }, [selectedRows]);

  const selectedStatuses = useMemo(() => {
    return Array.from(new Set(selectedRows.map((row) => row.status)));
  }, [selectedRows]);

  const isBulkDisabled = useMemo(() => {
    const hasAssigned = selectedStatuses.includes("Assigned");
    const hasInactive = selectedStatuses.includes("Inactive");
    const hasMaintenance = selectedStatuses.includes("Maintenance");

    if (hasAssigned && (hasInactive || hasMaintenance)) return true;
    if (hasAssigned && selectedStatuses.length === 1) return true;

    return false;
  }, [selectedStatuses]);

  const allowedBulkActions = useMemo(() => {
    const selectedCount = selectedKeys.length;
    if (selectedCount === 0) return [];
    if (isBulkDisabled) return [];

    const hasAvailable = selectedStatuses.includes("Available");
    const hasInactive = selectedStatuses.includes("Inactive");
    const hasMaintenance = selectedStatuses.includes("Maintenance");
    const hasAssigned = selectedStatuses.includes("Assigned");
    const hasDraft = selectedStatuses.includes("Draft");

    if (hasAvailable && (hasAssigned || hasMaintenance)) return [];

    const actions: string[] = [];

    if (selectedStatuses.length === 1) {
      if (hasInactive) actions.push("Available", "Maintenance", "Delete");
      if (hasAvailable) actions.push("Inactive", "Maintenance");
      if (hasMaintenance) actions.push("Available", "Inactive");
      if (draftMode && hasDraft) actions.push("Delete");
    } else if (
      selectedStatuses.length === 2 &&
      selectedStatuses.includes("Available") &&
      selectedStatuses.includes("Inactive")
    ) {
      actions.push("Maintenance");
    }

    return actions;
  }, [selectedKeys.length, isBulkDisabled, selectedStatuses]);

  return {
    selectedKeys,
    setSelectedKeys,
    selectedPlateNumbers,
    isBulkDisabled,
    allowedBulkActions,
  };
};
